import React from 'react';

/**
 * ArtworkSpecs
 *
 * The "spec sheet" block on `/artwork/:id` — edition, medium, dimensions,
 * available formats (clickable), year, availability. Extracted from
 * `ArtworkDetailPage.jsx` to keep the page < 600 lines.
 *
 * Props:
 *  - artwork: required — the artwork document
 *  - onFormatClick(format): invoked when a clickable format pill is pressed.
 *    The page wires this to its `PriceRequestModal` so the buyer can enquire
 *    about a specific paper/size variant.
 */
const ArtworkSpecs = ({ artwork, onFormatClick }) => {
  const hasFormats = Array.isArray(artwork.formats) && artwork.formats.length > 0;

  return (
    <div className="space-y-3 py-6 border-y border-neutral-200" data-testid="artwork-specs">
      {artwork.edition && (
        <div className="text-sm" data-testid="artwork-spec-edition">
          <span className="text-neutral-500">Edition: </span>
          <span className="font-medium">{artwork.edition}</span>
        </div>
      )}

      {artwork.medium && (
        <div className="text-sm" data-testid="artwork-spec-medium">
          <span className="text-neutral-500">Medium: </span>
          <span>{artwork.medium}</span>
        </div>
      )}

      {artwork.credit && (
        <div className="text-sm" data-testid="artwork-spec-credit">
          <span className="text-neutral-500">Credit: </span>
          <span>{artwork.credit}</span>
        </div>
      )}

      {artwork.dimensions && !hasFormats && (
        <div className="text-sm" data-testid="artwork-spec-dimensions">
          <span className="text-neutral-500">Size: </span>
          <span>{artwork.dimensions}</span>
        </div>
      )}

      {hasFormats && (
        <div className="text-sm" data-testid="artwork-spec-formats">
          <span className="text-neutral-500">Available formats: </span>
          <p className="text-xs text-neutral-400 mt-1 mb-2">
            Each size is a separate edition — click a size to check out that tier.
          </p>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {artwork.formats.map((f, i) => {
              const isAvailable = f.available !== false;
              return (
                <button
                  type="button"
                  key={f.sku || f.size || i}
                  disabled={!isAvailable}
                  onClick={() => onFormatClick && onFormatClick(f)}
                  className={`group flex items-baseline justify-between text-xs px-3 py-2 rounded border transition ${
                    isAvailable
                      ? 'border-neutral-300 text-neutral-800 hover:border-black hover:bg-neutral-50 cursor-pointer'
                      : 'border-neutral-200 text-neutral-500 cursor-not-allowed'
                  }`}
                  data-testid={`artwork-format-${i}`}
                >
                  <span className="font-medium">{f.size}</span>
                  <span className="flex items-baseline gap-2">
                    {isAvailable ? (
                      <>
                        {f.priceDisplay && (
                          <span data-testid={`artwork-format-price-${i}`}>
                            {f.priceDisplay}
                          </span>
                        )}
                        <span
                          aria-hidden="true"
                          className="text-neutral-400 group-hover:text-black transition"
                        >
                          →
                        </span>
                      </>
                    ) : (
                      <span
                        className="uppercase tracking-wider text-[10px] font-medium text-neutral-500"
                        data-testid={`artwork-format-sold-${i}`}
                      >
                        Sold Out
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {artwork.year && (
        <div className="text-sm" data-testid="artwork-spec-year">
          <span className="text-neutral-500">Year: </span>
          <span>{artwork.year}</span>
        </div>
      )}

      <div className="text-sm" data-testid="artwork-spec-availability">
        <span className="text-neutral-500">Availability: </span>
        {artwork.available ? (
          <span className="text-green-600 font-medium">Available</span>
        ) : (
          <span className="text-neutral-400">Sold</span>
        )}
      </div>
    </div>
  );
};

export default ArtworkSpecs;
